/**
 * Very similar to AppModalView but not a modal
 * Much of the code is the same minus a couple things - the main reason for not extending those methods
 * or the modalView itself, is that I want people to be able to customize them separately within the theme
 * It gets messy when you want your modal views to do something different than the fullscreen views
 * @class
 */
import Base from '@netx/core/lib/netx-multiView';
import {appRouter, getPrefixedLogger} from '@netx/core-next/instance';
import Template from '../templates/appFullScreenView.html';
import ModalView from './appModalView';
import ConfirmView from './confirmView';

const logger = getPrefixedLogger('views/appFullScreenView');

class AppFullScreenView extends Base {
	/** @override */
	constructor(opts = {}) {
		opts.additionalViewClasses = opts.additionalViewClasses || 'full-screen';
		opts.childView = _.extend(
			{
				target: '[data-nx-control="full-screen-content"]',
				viewClass(model) {
					return model.get('viewClass');
				},
			},
			opts.childView,
		);
		opts.emptyOnHide = opts.emptyOnHide !== false;
		opts.hideOnEmpty = opts.hideOnEmpty !== false;
		opts.id = opts.id || 'full-screen-view';
		opts.template = opts.template || Template;
		opts.removeOnHide = false;
		super(opts);

		// Hey, listen!
		this.listenTo(dispatcher, 'routed', this.hideIfWeShould).listenTo(
			this,
			'addedNewChildView',
			(view) => {
				if (view.viewSpec.isMyOriginalCollection(this.viewCollection)) {
					// Check if the view was a result of routing
					if (
						view.isRouted !== false &&
						Backbone.history.isRouting() &&
						!(view instanceof ConfirmView)
					) {
						view.isRouted = true;
					}
				}
			},
		);
	}

	/** @override */
	cancel(e) {
		// For now we are making the assumption that the closing of the fullscreen is dependant on its childViews
		// we need to get in the habit of using ":first" in our event object for childviews and cancel/close button
		// Do nothing when cancel is fired/bubbbled on this view
		if (e && e.preventDefault) {
			e.preventDefault();
			e.stopPropagation();
		}
		return false;
	}

	/** @override */
	postRender() {
		super.postRender();

		// Cache
		this.$content = this.$('[data-nx-control="full-screen-content"]');
	}

	/** @override */
	beforeHidden(cb) {
		// We do not call super
		if (this.emptyOnHide) {
			let view;
			this.viewCollection.each((viewSpec) => {
				view = viewSpec.get('view');
				view && _.isFunction(view.cancel) && view.cancel();
			});
			// Always use set to empty
			this.viewCollection.set([]);
		}

		// Fade out fullscreen and pipe finish to callback
		// this.$el.fadeOut("slow", _.bind( cb, this ) );
		this.$el.hide();
		cb.apply(this);
	}

	/**
	 * @override
	 * @param {function} cb Callback
	 */
	beforeShown(cb) {
		this.$el.show();
		cb.apply(this);
	}

	/** @method */
	hideIfWeShould(newRoute, oldRoute) {
		return ModalView.prototype.hideIfWeShould.call(this, newRoute, oldRoute);
	}

	/**
	 * Show is about to start
	 * At this point no animation or actual showing [should] have taken place
	 * @override
	 */
	onShowing() {
		super.onShowing();
		this.hiddenByRoute = false;
	}

	/**
	 * A view was instantiated (a brand new childView was born)
	 * @override
	 * @param {Netx.View} view Instance of Netx.View
	 */
	viewInstantiated(view) {
		super.viewInstantiated(view);

		// All true (non-transient) child views will be fully removed on hide
		if (!view.viewSpec.isMyOriginalCollection(this.viewCollection)) return;

		view.removeOnHide = true;
	}

	/**
	 * @override
	 * @param {Netx.View} view Instance of Netx.View
	 */
	viewRemoved(view) {
		if (!this.hiddenByRoute) {
			// If a new view came in in the mean time and it is routed - do nothing - we are at the route we should be
			const activeView = this.getActiveView();
			if (activeView && activeView.cid !== view.cid && activeView.isRouted) {
				return;
			}
			// HACK: preserve this value across callbacks:
			// (isRouting() reports false if a callback is fired via setTimeout(), such as in certain Bootstrap hiding animations.)
			const isRouting = Backbone.history.isRouting();
			// If we opened this modal view due to a route...
			if (view.isRouted) {
				logger.silly('removing routed view >', view.viewSpec.id);
				delete view.isRouted;
				// Unless we're currently routing, route back to where we were before we opened the modal.
				if (!isRouting) {
					view.routeMatch != null
						? appRouter.exitRoute(view.routeMatch, {force: true})
						: appRouter.back();
				}
			}
		}
		delete this.hiddenByRoute;

		super.viewRemoved(view);
	}

	/**
	 * @static
	 * @type {string}
	 */
	static get __name__() {
		return 'AppFullScreenView';
	}
}

export default AppFullScreenView;
